import { useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import "../game/game.css";
import { computeTeams, nextGame, rankTeams } from "../game/logic";
import useGameStore from "../game/store";

const Star = ({ color }: { color: string }) => (
  <svg viewBox="0 0 100 100" aria-hidden>
    <path
      d="M50 8 L62.5 33 L90.5 37 L70.5 57 L75.3 85 L50 71.7 L24.7 85 L29.5 57 L9.5 37 L37.5 33 Z"
      fill={color}
    />
  </svg>
);

export default function GameResultPage() {
  const navigate = useNavigate();
  const { session, restartSession, endSession } = useGameStore();

  const ranking = useMemo(
    () => (session ? rankTeams(computeTeams(session.teamNames, session.results)) : []),
    [session],
  );

  // sem partida (ex.: refresh) → configuração; partida ainda a decorrer → jogo
  if (!session) return <Navigate to="/" replace />;
  if (session.results.length < session.questions.length) {
    return <Navigate to="/jogo" replace />;
  }

  const winners = ranking.filter((t) => t.position === 1);
  const allTied = winners.length === ranking.length;

  const backToConfig = () => {
    endSession();
    navigate("/", { replace: true });
  };

  // Recomeça logo, com as mesmas equipes/tempo/tema e perguntas novas (sem novo pedido à API)
  const playAgain = () => {
    const teams = session.teamNames.length;
    const { questions, usedIds } = nextGame(
      session.quiz,
      session.questions.length / teams,
      teams,
      session.usedIds,
    );
    if (questions.length === 0) return;
    restartSession(questions, usedIds);
    navigate("/jogo", { replace: true });
  };

  return (
    <div className="qz-root">
      <div className="qz-result-wrap">
        <section className="qz-card qz-result" aria-labelledby="fim">
          <div className="qz-label" id="fim">Fim de jogo</div>

          {allTied ? (
            <div className="qz-tie">
              <div className="qz-star"><Star color="#B7B4C4" /></div>
              <div className="qz-winner-name qz-serif">Empate geral entre as equipes</div>
              <div className="qz-tie-dots" aria-hidden>
                {ranking.map((t) => (
                  <span key={t.index} className="qz-dot" style={{ background: t.color }} />
                ))}
              </div>
              <div className="qz-winner-points">{winners[0].score} {winners[0].score === 1 ? "ponto" : "pontos"} cada</div>
            </div>
          ) : (
            <>
              <div className="qz-label" style={{ marginTop: 14 }}>
                {winners.length > 1 ? "Equipes vencedoras" : "Equipe vencedora"}
              </div>
              <div className={`qz-winners ${winners.length > 1 ? "is-multi" : ""}`}>
                {winners.map((w) => (
                  <div className="qz-winner" key={w.index}>
                    <div className="qz-star"><Star color={w.color} /></div>
                    <div className="qz-winner-name qz-serif">{w.name}</div>
                    <div className="qz-winner-points">
                      {w.score} {w.score === 1 ? "ponto" : "pontos"}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <hr className="qz-hr" />

          <div className="qz-label" style={{ alignSelf: "flex-start" }}>Classificação</div>
          <ol className="qz-rank">
            {ranking.map((t) => (
              <li key={t.index} className={`qz-rank-item ${t.position === 1 ? "is-first" : ""}`}>
                <span className="qz-rank-pos">{t.position}º</span>
                <span className="qz-dot qz-dot-sm" style={{ background: t.color }} />
                <span>{t.name}</span>
                <span className="qz-rank-pts">{t.score} {t.score === 1 ? "pt" : "pts"}</span>
              </li>
            ))}
          </ol>

          <div className="qz-result-actions">
            <button type="button" className="qz-btn qz-btn-primary" onClick={playAgain}>
              Jogar novamente
            </button>
            <button type="button" className="qz-btn qz-btn-outline" onClick={backToConfig}>
              Voltar à configuração
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
